import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth-client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const signUpSchema = z
	.object({
		name: z
			.string()
			.trim()
			.min(2, { message: "Name must be at least 2 characters." })
			.max(30, { message: "Name must be at most 30 characters." })
			.regex(/^[a-zA-Z0-9\s'-]+$/, {
				message: "Name can only contain letters, spaces, ' and -",
			}),
		email: z.email(),
		password: z.string().min(6, "Password must be at least 6 characters"),
		confirmPassword: z.string(),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

type SignUpFormData = z.infer<typeof signUpSchema>;

interface SignUpFormProps extends React.ComponentProps<"div"> {
	redirectUrl?: string;
}

export function SignUpForm({
	className,
	redirectUrl,
	...props
}: SignUpFormProps) {
	const router = useRouter();
	const [serverError, setServerError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const form = useForm<SignUpFormData>({
		resolver: zodResolver(signUpSchema),
		defaultValues: {
			name: "",
			email: "",
			password: "",
			confirmPassword: "",
		},
	});

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = form;

	const onSubmit = async (data: SignUpFormData) => {
		setIsLoading(true);
		setServerError("");

		try {
			const res = await authClient.signUp.email({
				name: data.name,
				email: data.email,
				password: data.password,
			});

			if (res.error || !res.data?.user || !res.data.user.lastWorkspaceId) {
				const msg = res.error?.message || "Registration failed";
				setServerError(msg);
				toast.error(msg);
				return;
			}

			const lastWorkspaceId = res.data.user.lastWorkspaceId;

			toast.success("Account created!");
			if (redirectUrl) {
				await router.navigate({ to: redirectUrl });
			} else {
				await router.navigate({
					to: "/$workspaceId",
					params: { workspaceId: lastWorkspaceId },
				});
			}
		} catch (err) {
			const msg = err instanceof Error ? err.message : "Something went wrong.";
			setServerError(msg);
			toast.error(msg);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className={cn("flex flex-col gap-6", className)} {...props}>
			<Card className="overflow-hidden p-0">
				<CardContent className="grid grid-cols-1 p-0 md:grid-cols-2">
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="flex flex-col gap-6 p-6 md:p-8"
					>
						<div className="text-center">
							<h1 className="font-bold text-2xl">Create your account</h1>
							<p className="text-muted-foreground text-sm">
								Sign up to get started
							</p>
						</div>

						<div className="grid gap-3">
							<Label htmlFor="name">Full name</Label>
							<Input id="name" placeholder="John Doe" {...register("name")} />
							{errors.name && (
								<p className="text-red-500 text-sm">{errors.name.message}</p>
							)}
						</div>

						<div className="grid gap-3">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								placeholder="m@example.com"
								{...register("email")}
							/>
							{errors.email && (
								<p className="text-red-500 text-sm">{errors.email.message}</p>
							)}
						</div>

						<div className="grid gap-3">
							<Label htmlFor="password">Password</Label>
							<div className="relative">
								<Input
									id="password"
									type={showPassword ? "text" : "password"}
									placeholder="••••••••"
									{...register("password")}
									className="pr-10"
								/>
								<button
									type="button"
									onClick={() => setShowPassword((prev) => !prev)}
									className="-translate-y-1/2 absolute top-1/2 right-3 text-muted-foreground hover:text-foreground"
									aria-label={showPassword ? "Hide password" : "Show password"}
								>
									{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
								</button>
							</div>
							{errors.password && (
								<p className="text-red-500 text-sm">
									{errors.password.message}
								</p>
							)}
						</div>

						<div className="grid gap-3">
							<Label htmlFor="confirmPassword">Confirm Password</Label>
							<Input
								id="confirmPassword"
								type={showPassword ? "text" : "password"}
								placeholder="••••••••"
								{...register("confirmPassword")}
							/>
							{errors.confirmPassword && (
								<p className="text-red-500 text-sm">
									{errors.confirmPassword.message}
								</p>
							)}
						</div>

						{serverError && (
							<p className="text-center text-red-500 text-sm">{serverError}</p>
						)}

						<Button type="submit" className="w-full" disabled={isLoading}>
							{isLoading ? "Creating account..." : "Sign up"}
						</Button>

						<div className="text-center text-sm">
							Already have an account?{" "}
							<button
								type="button"
								onClick={() => {
									const searchParams = redirectUrl
										? `?redirect=${encodeURIComponent(redirectUrl)}`
										: "";
									router.navigate({ to: `/sign-in${searchParams}` });
								}}
								className="underline underline-offset-4 hover:text-primary"
							>
								Sign in
							</button>
						</div>
					</form>

					<div className="relative hidden bg-muted md:block">
						<img
							src="https://images.pexels.com/photos/2041627/pexels-photo-2041627.jpeg"
							alt="Signup image"
							className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
						/>
					</div>
				</CardContent>
			</Card>

			<div className="text-balance text-center text-muted-foreground text-xs *:[a]:underline *:[a]:underline-offset-4 *:[a]:hover:text-primary">
				By continuing, you agree to our <a href="#">Terms of Service</a> and{" "}
				<a href="#">Privacy Policy</a>.
			</div>
		</div>
	);
}
